import { getAccessToken } from "@/lib/api"

const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL) ||
  "http://127.0.0.1:8787"

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
}

interface MessageResponseData {
  message: string
  quick_actions?: string[]
  insight_card?: {
    type: "revenue" | "alert"
    label: string
    value: string
    value_color: string
    description: string
    link_text: string
    link_href: string
  }
}

interface ProactiveResponseData {
  has_message: boolean
  action_id?: string
  action_type?: string
  priority?: string
  title?: string
  body?: string
  cta?: string
  badge_type?: "alert" | "insight"
}

interface ProactiveOpenResponseData {
  opened: boolean
  content?: {
    body: string
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<ApiResponse<T>> {
  try {
    const token = getAccessToken()
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options?.headers,
      },
    })
    const json = (await res.json()) as { ok?: boolean; error?: string } & T
    // Map backend { ok } to frontend { success, data }
    return {
      success: Boolean(json.ok),
      data: json.ok ? (json as T) : undefined,
      error: json.ok ? undefined : json.error,
    }
  } catch {
    return { success: false, error: "Network error" }
  }
}

export const conciergeClient = {
  sendMessage(
    message: string,
    pageContext: { current_page: string; property_id?: string },
  ) {
    return request<MessageResponseData>("/api/concierge/message", {
      method: "POST",
      body: JSON.stringify({ message, page_context: pageContext }),
    })
  },

  getProactive() {
    return request<ProactiveResponseData>("/api/concierge/proactive")
  },

  dismissProactive(actionId: string) {
    return request<{ dismissed: boolean }>("/api/concierge/proactive/dismiss", {
      method: "POST",
      body: JSON.stringify({ action_id: actionId }),
    })
  },

  openProactive(actionId: string) {
    return request<ProactiveOpenResponseData>("/api/concierge/proactive/open", {
      method: "POST",
      body: JSON.stringify({ action_id: actionId }),
    })
  },
}

export type { MessageResponseData, ProactiveResponseData, ProactiveOpenResponseData }
